<template>
  <div
    id="video-player-container"
    ref="videoPlayerContainer"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div
      id="video-player"
      class="d-flex justify-center"
      v-if="audioOnly"
      v-on:click="togglePlayAndPause"
    >
      <v-icon dark x-large>mdi-volume-low</v-icon>
    </div>
    <video
      id="video-player"
      ref="videoPlayer"
      v-else
      v-bind:muted="videoMuted"
      v-on:contextmenu.prevent
      v-on:click="togglePlayAndPause"
    >
    </video>
    <div
      id="video-title"
      class="h6 font-weight-black overlay-text-box"
      v-show="(videoTitle !== undefined) && $data.$_isControllerShown"
    >
      {{ videoTitle }}
    </div>
    <video-player-controller
      id="video-player-controller"
      v-if="$data.$_player"
      v-bind:shows="$data.$_isControllerShown && $data.$_isLoaded"
      v-bind:duration="$_duration"
      v-bind:play-time="$_playTime"
      v-bind:video-time-range="videoTimeRange"
      v-bind:video-playback-rate.sync="$_videoPlaybackRate"
      v-bind:video-volume-rate.sync="$_videoVolumeRate"
      v-bind:video-muted.sync="$_videoMuted"
      v-bind:is-playing="$_isPlaying"
      v-bind:is-loop-enabled.sync="$_isLoopEnabled"
      v-bind:loop-definition="loopDefinition"
      v-bind:sorted-marker-segments="sortedMarkerSegments"
      v-on:seek="$_seek"
      v-on:play="$_play"
      v-on:pause="$_pause"
      v-on:set-playback-rate="$_setPlaybackRate"
    >
    </video-player-controller>
  </div>
</template>

<style scoped>
#video-player-container {
  position: relative;
  background-color: #000000;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

#video-title {
  position: absolute;
  padding: 2px 10px 2px 15px;
  top: 10px;
  left: 0;
}

#video-player {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

#video-player-controller {
  position: absolute;
  left: 0px;
  bottom: 0px;
  width: 100%;
}
</style>

<style src="./VideoPlayer/video_player.css"></style>

<script>
import VideoPlayerController from './VideoPlayer/VideoPlayerController.vue';
import LoopDefinition from '../modules/LoopDefinition.js';
import mouseEventUtils from '../modules/mouseEventUtils.js';
import { TimeValue, TimeUnit } from '../modules/TimeValue.js';
import VideoTimeRange from './VideoPlayer/modules/VideoTimeRange.js';

class VideoMetaData {
  static name = 'VideoMetaData';

  constructor(duration) {
    this.duration = duration;
  }
}

class VideoPlayInfo {
  static name = 'VideoPlayInfo';

  constructor() {
    this.playTime = TimeValue.zero;
    this.isPlaying = false;
  }
}

export default {
  components: {
    VideoPlayerController,
  },

  watch: {
    videoUrl(newVideoUrl) {
      if (newVideoUrl) this.$_loadVideoSource(newVideoUrl);
    },

    '$data.$_playInfo': {
      handler() { this.$emit('play-info-updated', this.$data.$_playInfo); },
      deep: true,
    },
  },

  props: {
    videoTitle:           { type: String },
    videoUrl:             { type: String },
    videoTimeRange:       { type: VideoTimeRange },
    videoPlaybackRate:    { type: Number },
    videoVolumeRate:      { type: Number },
    videoMuted:           { type: Boolean },
    isLoopEnabled:        { type: Boolean },
    loopDefinition:       { type: LoopDefinition },
    sortedMarkerSegments: { type: Array },
    audioOnly:            { type: Boolean },
  },

  data() {
    return {
      $_player: undefined,
      $_videoMetaData: undefined,
      $_playInfo: new VideoPlayInfo(),
      $_playbackBeginTime: undefined,
      $_isControllerShown: false,
      $_isLoaded: false,
    };
  },

  mounted() {
    if (this.audioOnly) {
      this.$data.$_player = document.createElement('audio');
    } else {
      this.$data.$_player = this.$refs.videoPlayer;
    }
    this.$nextTick(() => {
      if (this.$data.$_player === undefined) return;

      this.$data.$_playbackBeginTime = undefined;

      this.$data.$_player.onloadedmetadata = () => {
        this.$data.$_isLoaded = true;
        this.$data.$_videoMetaData = new VideoMetaData(TimeValue.sec(this.$data.$_player.duration).floor(TimeUnit.units.msec));
        this.$emit('meta-data-loaded', this.$data.$_videoMetaData);

        let previousTime = this.$_getPlayTime();
        const dispatchAnimationFrameUpdateEvent = () => {
          let currentTime = this.$_getPlayTime();
          if (!previousTime.isEqualTo(currentTime)) {
            previousTime = currentTime;
            this.$data.$_player.dispatchEvent(new CustomEvent('animationframeupdate'));
          }
          window.requestAnimationFrame(dispatchAnimationFrameUpdateEvent);
        }
        window.requestAnimationFrame(dispatchAnimationFrameUpdateEvent);

        this.$data.$_player.addEventListener('animationframeupdate', this.$_update);
        this.$data.$_player.ontimeupdate = this.$_update;
      };

      this.$data.$_player.onemptied = () => {
        this.$data.$_player.removeEventListener('animationframeupdate', this.$_update);
        this.$data.$_player.ontimeupdate = undefined;
        this.$data.$_isLoaded = false;
      };

      this.$data.$_player.onplaying = () => {
        if (!this.$_isPlaybackBegun) {
          this.$_beginPlayback(this.$_getPlayTime());
        }
      };

      this.$data.$_player.onended = async () => {
        if (this.isLoopEnabled && this.$_isPlaying) {
          this.$_seek(this.loopDefinition.begin);
          await this.$_play();
        } else {
          this.$_pause();
        }
      };

      this.$data.$_player.onpause = () => { this.$_fixPlayback(this.$_getPlayTime()) };

      if (this.videoUrl) this.$_loadVideoSource(this.videoUrl);
    });
  },

  computed: {
    /* private */
    $_playTime() {
      return this.$data.$_playInfo.playTime;
    },
    $_isPlaying() {
      return this.$data.$_playInfo.isPlaying;
    },
    $_duration() {
      if (this.$data.$_videoMetaData === undefined) return undefined;
      return this.$data.$_videoMetaData.duration;
    },
    $_isPlaybackBegun() {
      return this.$data.$_playbackBeginTime !== undefined;
    },
    $_videoPlaybackRate: {
      get() { return this.videoPlaybackRate },
      set(videoPlaybackRate) {
        this.$data.$_player.playbackRate = videoPlaybackRate;
        this.$emit('update:video-playback-rate', videoPlaybackRate);
      },
    },
    $_videoVolumeRate: {
      get() { return this.videoVolumeRate },
      set(videoVolumeRate) {
        this.$data.$_player.volume = videoVolumeRate;
        this.$emit('update:video-volume-rate', videoVolumeRate);
      },
    },
    $_videoMuted: {
      get() { return this.videoMuted },
      set(videoMuted) {
        this.$data.$_player.muted = videoMuted;
        this.$emit('update:video-muted', videoMuted);
      },
    },
    $_isLoopEnabled: {
      get()              { return this.isLoopEnabled },
      set(isLoopEnabled) { this.$emit('update:is-loop-enabled', isLoopEnabled) },
    },
  },

  methods: {
    /* public */
    onMousemove(event) {
      this.$data.$_isControllerShown = mouseEventUtils.isOverElement(event, { element: this.$refs.videoPlayerContainer });
    },

    async togglePlayAndPause() {
      if (!this.$data.$_isLoaded) return;
      await this.$_togglePlayAndPause();
    },

    seek(seekTime) {
      if (!this.$data.$_isLoaded) return;
      this.$_seek(seekTime);
    },

    /* private */
    async $_togglePlayAndPause() {
      if (this.$_isPlaying) {
        this.$_pause();
      } else {
        await this.$_play();
      }
    },

    async $_play() {
      let playTime = this.$_getPlayTime();
      if (this.$data.$_player.ended || playTime.isGreaterThanOrEqualTo(this.videoTimeRange.end)) {
        this.$_seek(this.videoTimeRange.begin);
      }
      this.$_beginPlayback(this.$_getPlayTime());
      await this.$data.$_player.play();
    },

    $_pause() { this.$data.$_player.pause() },

    $_seek(time) {
      if (this.$_isPlaying) this.$_fixPlayback(this.$_getPlayTime());
      this.$data.$_player.currentTime = time.sec;
      if (this.$_isPlaying) this.$_beginPlayback(time);
    },

    $_getPlayTime() {
      return TimeValue.sec(this.$data.$_player.currentTime).floor(TimeUnit.units.msec);
    },

    $_loadVideoSource(videoUrl) {
      let previousPlaybackRate = this.$data.$_player.playbackRate;
      this.$_pause();
      this.$data.$_player.src = videoUrl;
      this.$data.$_player.volume = this.videoVolumeRate;
      this.$data.$_player.playbackRate = previousPlaybackRate;
      this.$_isLoopEnabled = false;
    },

    $_setPlaybackRate(playbackRate) {
      this.$data.$_player.playbackRate = playbackRate;
    },

    $_update() {
      let playTime = this.$_getPlayTime();
      if (this.isLoopEnabled) {
        let flooredLoopBegin = this.loopDefinition.begin.floorTo(this.videoTimeRange.begin);
        let ceiledLoopEnd = this.loopDefinition.end.ceilTo(this.videoTimeRange.end);
        if (playTime.isLessThan(flooredLoopBegin) || playTime.isGreaterThanOrEqualTo(ceiledLoopEnd)) {
          this.$_seek(flooredLoopBegin);
          playTime = flooredLoopBegin;
        }
      } else {
        if (playTime.isLessThan(this.videoTimeRange.begin)) {
          this.$_seek(this.videoTimeRange.begin);
          playTime = this.videoTimeRange.begin;
        } else if (playTime.isGreaterThanOrEqualTo(this.videoTimeRange.end)) {
          this.$_seek(this.videoTimeRange.end);
          playTime = this.videoTimeRange.end;
        }
      }
      this.$data.$_playInfo.playTime.assign(playTime);
      this.$data.$_playInfo.isPlaying = !this.$data.$_player.paused && !this.$data.$_player.ended;
    },

    $_beginPlayback(playbackBeginTime) {
      this.$data.$_playbackBeginTime = playbackBeginTime;
    },

    $_fixPlayback(playbackEndTime) {
      if (this.$_isPlaybackBegun) {
        this.$emit('playback-fixed', this.$data.$_playbackBeginTime, playbackEndTime);
        this.$data.$_playbackBeginTime = undefined;
      }
    },
  },
};
</script>
